import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, Zap, LogOut, ChevronDown } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

export default function Header() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useLang();
  const [open, setOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const navigate = useNavigate();

  const items = [
    { to: "/marketplace", label: t("nav.marketplace") },
    { to: "/docs", label: t("nav.docs") },
    { to: "/status", label: t("nav.status") },
  ];

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0a0b0e]/70 border-b border-white/8">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#d4af37] to-[#a3852c] shadow-lg shadow-[#d4af37]/20">
            <Zap className="w-4 h-4 text-[#0a0b0e]" strokeWidth={3} />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display font-semibold text-white tracking-tight text-lg">
              LoftPress
            </span>
            <span className="text-[10px] text-white/40 font-mono tracking-wider uppercase">
              {t("brand.tagline")}
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8 flex-1 justify-center">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              className={({ isActive }) => `lp-nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${it.to.replace(/\//g, "")}`}
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={() => setLang(lang === "en" ? "tr" : "en")}
            className="text-xs font-mono uppercase tracking-widest text-white/60 hover:text-[#d4af37] transition-colors px-2 py-1 rounded-md border border-white/10"
            aria-label="Switch language"
          >
            {lang === "en" ? "EN · TR" : "TR · EN"}
          </button>

          {user === null && (
            <div className="w-24 h-9 rounded-full lp-shimmer" />
          )}

          {user === false && (
            <>
              <Link to="/login" className="lp-nav-link" data-testid="nav-signin">
                {t("nav.signin")}
              </Link>
              <Link to="/register" className="lp-btn-primary" data-testid="nav-getstarted">
                {t("nav.getstarted")}
              </Link>
            </>
          )}

          {user && typeof user === "object" && (
            <div className="relative">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/10 hover:border-[#d4af37]/40 transition-colors"
                data-testid="user-menu-btn"
              >
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#d4af37] to-[#8b6f24] flex items-center justify-center text-[10px] font-semibold text-[#0a0b0e]">
                  {(user.name || user.email)[0]?.toUpperCase()}
                </div>
                <span className="text-sm text-white/80 hidden lg:inline">{user.name}</span>
                <ChevronDown className="w-3 h-3 text-white/50" />
              </button>
              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-52 lp-glass p-2 shadow-2xl"
                  onMouseLeave={() => setMenuOpen(false)}
                  data-testid="user-menu-dropdown"
                >
                  <Link
                    to="/dashboard"
                    onClick={() => setMenuOpen(false)}
                    className="lp-sidebar-link"
                    data-testid="menu-dashboard"
                  >
                    {t("nav.dashboard")}
                  </Link>
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setMenuOpen(false)}
                      className="lp-sidebar-link"
                      data-testid="menu-admin"
                    >
                      {t("nav.admin")}
                    </Link>
                  )}
                  <button
                    onClick={async () => {
                      await logout();
                      setMenuOpen(false);
                      navigate("/");
                    }}
                    className="lp-sidebar-link w-full text-left"
                    data-testid="menu-logout"
                  >
                    <LogOut className="w-4 h-4" /> {t("nav.logout")}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <button
          className="lg:hidden text-white/70 p-2"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="mobile-menu-toggle"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/8 px-6 py-4 space-y-3 bg-[#0a0b0e]/95">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              onClick={() => setOpen(false)}
              className="block lp-nav-link"
              data-testid={`mobile-nav-${it.to.replace(/\//g, "")}`}
            >
              {it.label}
            </NavLink>
          ))}
          <div className="flex gap-3 pt-3 border-t border-white/8">
            {(!user || user === false) ? (
              <>
                <Link to="/login" className="lp-btn-secondary flex-1 justify-center" onClick={() => setOpen(false)}>
                  {t("nav.signin")}
                </Link>
                <Link to="/register" className="lp-btn-primary flex-1 justify-center" onClick={() => setOpen(false)}>
                  {t("nav.getstarted")}
                </Link>
              </>
            ) : (
              <Link to="/dashboard" className="lp-btn-primary flex-1 justify-center" onClick={() => setOpen(false)}>
                {t("nav.dashboard")}
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
